import { cast, getSnapshot, types } from "mobx-state-tree"
import { DictionaryLink } from "modules/common/models/dictionary-link"
import { flow } from "modules/common/models/flow"
import { Notificator } from "modules/common/models/notificator"
import { Transport } from "modules/common/models/transport"
import { apiUrls } from "modules/common/services/communication/urls"

/**
 * Переписка с агентами по почте: цепочки писем, письма открытой цепочки, отправка.
 *
 * Сущности — с идентификатором (цепочка — key, письмо — id), список цепочек и письма
 * открытой цепочки — массивы, узлы которых переживают перезагрузку. Компоненты берут
 * их из стора, а не получают узлом в пропсах.
 */

export const DELIVERY_PENDING = "Pending"
export const DELIVERY_SENDING = "Sending"
export const DELIVERY_SENT = "Sent"
export const DELIVERY_FAILED = "Failed"

export const MODE_ALL = "all"
export const MODE_NO_ORDER = "noOrder"
export const MODE_UNASSIGNED = "unassigned"

const PAGE_SIZE = 30

const MailAgent = types.model({
  id: types.string,
  name: types.optional(types.string, ""),
  email: types.optional(types.maybeNull(types.string), ""),
})

export const AgentMailThread = types
  .model({
    key: types.identifier,
    subject: types.optional(types.maybeNull(types.string), ""),
    tag: types.maybeNull(types.string),
    order: types.maybeNull(DictionaryLink),
    lastAt: types.string,
    lastTime: types.optional(types.string, ""),
    count: types.number,
    unread: types.number,
    failed: types.number,
    agents: types.array(MailAgent),
    unassigned: types.boolean,
    senderEmail: types.maybeNull(types.string),
    senderName: types.maybeNull(types.string),
    lastIncoming: types.boolean,
    lastAuthor: types.optional(types.maybeNull(types.string), ""),
    preview: types.optional(types.maybeNull(types.string), ""),
  })
  .named("AgentMailThread")

const Delivery = types.model({
  email: types.string,
  name: types.optional(types.maybeNull(types.string), ""),
  status: types.optional(types.maybeNull(types.string), DELIVERY_PENDING),
  error: types.optional(types.maybeNull(types.string), ""),
})

export const AgentMailPost = types
  .model({
    id: types.identifier,
    created: types.string,
    date: types.string,
    time: types.string,
    isRequest: types.boolean,
    subject: types.optional(types.maybeNull(types.string), ""),
    message: types.optional(types.maybeNull(types.string), ""),
    employer: types.maybeNull(DictionaryLink),
    recipients: types.array(MailAgent),
    order: types.maybeNull(DictionaryLink),
    attachments: types.array(types.model({ id: types.string, name: types.string })),
    deliveries: types.array(Delivery),
    senderEmail: types.maybeNull(types.string),
    senderName: types.maybeNull(types.string),
    unread: types.optional(types.boolean, false),
    unassigned: types.optional(types.boolean, false),
  })
  .named("AgentMailPost")

export interface AgentMailFilter {
  /** Договор: задан — показываем только его переписку */
  orderId: string
  /** Объекты из фильтра списка: можно выбрать несколько */
  orderIds: string[]
  mode: string
  agent: string
  unreadOnly: boolean
  search: string
}

export interface SendAgentMail {
  threadKey?: string
  order?: { id: string; name: string } | null
  agents: string[]
  subject: string
  message: string
  files: string[]
}

export const AgentMailStore = types
  .compose(
    Transport,
    Notificator,
    types.model({
      threads: types.array(AgentMailThread),
      total: types.number,
      /** Ключ открытой цепочки */
      current: types.maybeNull(types.string),
      posts: types.array(AgentMailPost),
      /** Цепочек с непросмотренными ответами */
      unread: types.number,
    })
  )
  .volatile(() => ({
    filter: {
      orderId: "",
      orderIds: [],
      mode: MODE_ALL,
      agent: "",
      unreadOnly: false,
      search: "",
    } as AgentMailFilter,
    page: 0,
    loadingThreads: false,
    loadingPosts: false,
  }))
  .views((self) => ({
    get currentThread() {
      return self.threads.find((t) => t.key === self.current) ?? null
    },
    get hasMore() {
      return self.threads.length < self.total
    },
  }))
  .actions((self) => {
    // ответ на устаревший запрос (сменили фильтр или цепочку) не должен перетереть свежий
    let threadsRun = 0
    let postsRun = 0

    const query = (page: number) => ({ ...self.filter, page, pageSize: PAGE_SIZE })

    return {
      setFilter(patch: Partial<AgentMailFilter>) {
        self.filter = { ...self.filter, ...patch }
      },

      loadThreads: flow(function* (more = false) {
        const run = ++threadsRun
        const page = more ? self.page + 1 : 0
        self.loadingThreads = true

        try {
          const data: { items: any[]; total: number } = yield self.transport.post<any>(
            apiUrls.agentMail.threads(),
            query(page)
          )

          if (run !== threadsRun) {
            return
          }

          self.threads = cast(more ? [...getSnapshot(self.threads), ...data.items] : data.items)
          self.total = data.total
          self.page = page
        } catch (er) {
          self.notify.error(er)
        } finally {
          if (run === threadsRun) {
            self.loadingThreads = false
          }
        }
      }),

      loadUnread: flow(function* () {
        try {
          const count: number = yield self.transport.get<number>(
            apiUrls.agentMail.unread(self.filter.orderId || undefined)
          )
          self.unread = count
        } catch {
          // счётчик необязателен: без него переписка работает
        }
      }),

      /** Открыть цепочку (null — закрыть) */
      open: flow(function* (key: string | null) {
        const run = ++postsRun
        self.current = key

        if (!key) {
          self.posts = cast([])
          return
        }

        self.loadingPosts = true

        try {
          const posts = (yield self.transport.get<any[]>(apiUrls.agentMail.thread(key))) as any[]

          if (run !== postsRun) {
            return
          }

          self.posts = cast(posts)

          const thread = self.threads.find((t) => t.key === key)
          if (posts.some((p) => p.unread)) {
            yield self.transport.post<boolean>(apiUrls.agentMail.read(key), {})

            if (thread && thread.unread > 0) {
              thread.unread = 0
              self.unread = Math.max(0, self.unread - 1)
            }
          }
        } catch (er) {
          self.notify.error(er)
        } finally {
          if (run === postsRun) {
            self.loadingPosts = false
          }
        }
      }),
    }
  })
  .actions((self) => ({
    /** Отправить письмо; возвращает ключ цепочки или null при ошибке */
    send: flow(function* (model: SendAgentMail) {
      try {
        const key = (yield self.transport.post<string>(apiUrls.agentMail.send(), model)) as string

        self.notify.success("Письмо поставлено в очередь на отправку")

        yield self.loadThreads()
        yield self.open(key)

        return key
      } catch (er) {
        self.notify.error(er)
        return null
      }
    }),

    resend: flow(function* (post: string, email: string) {
      try {
        yield self.transport.post<boolean>(apiUrls.agentMail.resend(), { post, email })
        self.notify.success("Письмо снова поставлено в очередь")

        if (self.current) {
          yield self.open(self.current)
        }

        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    attach: flow(function* (key: string, order: { id: string; name: string } | null, agent: string | null) {
      try {
        yield self.transport.post<boolean>(apiUrls.agentMail.attach(key), { order, agent })
        self.notify.success("Переписка привязана")

        yield self.loadThreads()
        yield self.open(key)

        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    /** Забрать новые письма из почты сейчас */
    sync: flow(function* () {
      try {
        yield self.transport.post<boolean>(apiUrls.agentMail.sync(), {})

        yield self.loadThreads()
        yield self.loadUnread()

        if (self.current) {
          yield self.open(self.current)
        }

        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    uploadFile: flow(function* (file: File, accept: string) {
      try {
        const model = new FormData()
        model.append("file", file)
        model.append("accept", accept)

        const result: { id: string; previewMimeType: string; mimeType: string } = yield self.transport.post<any>(
          apiUrls.application.files.upload,
          model
        )

        const fileBase: FileBase = {
          fileId: result.id,
          fileName: file.name,
          previewMimeType: result.previewMimeType,
          mimeType: result.mimeType,
        }

        return fileBase
      } catch (er) {
        self.notify.error(er)
        return null
      }
    }),
  }))
  .named("AgentMailStore")

export const initialState = (): AgentMailStoreSnapshotType => ({
  threads: [],
  total: 0,
  current: null,
  posts: [],
  unread: 0,
})

export type AgentMailStoreSnapshotType = typeof AgentMailStore.SnapshotType
export type AgentMailStoreType = typeof AgentMailStore.Type
export type AgentMailThreadType = typeof AgentMailThread.Type
export type AgentMailPostType = typeof AgentMailPost.Type
